import { ProfileView }  from "@authory-packages/core";
import styled, { css, RuleSet } from "styled-components";
import { media, theme } from "../../theme/theme";
import { EmbedTheme } from "../../utils/embeds";
import { V3Body, V3HeadlineH3, V3Paragraph } from "../Typography";
import { ClampMixin, ClampMixinType } from "../ReusableStyledComponents/ClampMixin";
import { getCssSafeUrl } from "../../utils/getSafeUrlCss";
import { AuthoryColorSchemeProps, UserThemableHeading, UserThemableParagraph } from "../ReusableStyledComponents/FontSetUtilities";
import { hexToRgba } from "../../utils/hexToRGBA";
import { CUSTOM_FONT_SET_SIZES } from "../../utils/getFontSetLabels";
import { VerticalSpacing } from "../VerticalSpacing";

export type ResponsiveViewProps = { $viewType: ProfileView; $breakpoint?: number };

/**
 * Renders a view as one column below the breakpoint and as itself from the breakpoint up.
 * @param viewType The configured view
 * @param breakpoint Width from which the view applies, omit to always apply it
 * @param styles Builds the declarations for a view
 * @returns The declarations, wrapped in a media query when the view collapses
 */
export function responsiveView(viewType: ProfileView, breakpoint: number | undefined, styles: (view: ProfileView) => RuleSet<object>) {
    if (!breakpoint) return styles(viewType);

    return css`
        ${styles(ProfileView.OneColumnThumbnail)}

        ${media.custom(breakpoint)} {
            ${styles(viewType)}
        }
    `;
}

export const PLIFavicon = styled.div<{ $faviconUrl?: string }>`
    background-image: ${({ $faviconUrl }) => $faviconUrl ? getCssSafeUrl($faviconUrl): null };
    background-size: contain;
    background-repeat: no-repeat;
    height: 16px;
    width: 16px;
    margin-right: 10px;
    flex-shrink: 0;
`;

export const UserThemableMetadataText = styled(V3Body)<{$allowUserThemable: boolean} & AuthoryColorSchemeProps >`
    ${({ $allowUserThemable }) => $allowUserThemable && UserThemableHeading}
`;

export const PLIMetadataText = styled(UserThemableMetadataText)<{$allowUserThemable: boolean}>`
    text-overflow: ellipsis;
    white-space: nowrap;
    width: calc(100%);
    overflow: hidden;
    font-weight: 400;
    font-size: ${({ theme,  $allowUserThemable }) => {
        if (!$allowUserThemable) return undefined;
        if (CUSTOM_FONT_SET_SIZES.includes(theme.customization.profileFontSet)) {
            return "15px";
        }
    }};

    // this applies only when the component is used outside the user themable realm, hence the check for  allowUserThemable
    color: ${({ theme, $allowUserThemable }) => {
        if(!$allowUserThemable) return theme.v3.colors.grey48;
    }};
`;

export const PLIMetadata = styled.div<{ $isEmbed: boolean, $embedTheme: EmbedTheme }>`
    display: flex;
    align-items: center;
    max-width: calc(100vw - 52px);

    ${media.phone} {
        max-width: calc(100%);
    }

    ${PLIMetadataText} {
        font-size: ${({ $isEmbed }) => $isEmbed ? "12px" : undefined};
        line-height: ${({ $isEmbed }) => $isEmbed ? "16px" : undefined};
    }
`;

export const ItemTextWrapper = styled.div<ResponsiveViewProps & { $isPinned?: boolean }>`
    ${({ $viewType, $breakpoint, $isPinned }) => responsiveView($viewType, $breakpoint, (view) => css`
        padding: ${() => {
            if (view === ProfileView.Masonry) return "24px 30px";
            if (view === ProfileView.OneColumnThumbnail && $isPinned) return "0 18px 18px";
            if (view === ProfileView.TwoColumnThumbnail && $isPinned) return "32px 12px 12px 48px";
            if (view === ProfileView.List && $isPinned) return "18px";
            return null;
        }};
        width: ${view === ProfileView.TwoColumnThumbnail && $isPinned ? "50%" : null};
    `)}
`;

export const ItemImageWrapper = styled.div<ResponsiveViewProps & { $isPinned?: boolean }>`
    ${({ $viewType, $breakpoint, $isPinned }) => responsiveView($viewType, $breakpoint, (view) => css`
        width: ${view === ProfileView.TwoColumnThumbnail && $isPinned ? "50%" : null};
        display: ${view === ProfileView.List ? "none" : null};
    `)}

    a {
        display: block;
        width: 100%;
    }
`;

export const PLIThumbnailPad = styled.div<ResponsiveViewProps & { $isPinned?: boolean, $isProfileMock?: boolean }>`
    ${({ $viewType, $breakpoint, $isPinned, $isProfileMock }) => responsiveView($viewType, $breakpoint, (view) => css`
        padding-bottom: ${view === ProfileView.Masonry || $isProfileMock || (view === ProfileView.TwoColumnThumbnail && $isPinned) ? "0" : "18px"};
    `)}
`;

export const PLIThumbnail = styled.div<ResponsiveViewProps & { $isPinned?: boolean, $imageRatio?: string, $isProfileMock?: boolean}>`
    position: relative;
    overflow: hidden;
    height: 0;
    transition: background-color 0.3s ease, border-color 0.3s ease;

    ${({ $viewType, $breakpoint, $isPinned, $imageRatio, $isProfileMock }) => responsiveView($viewType, $breakpoint, (view) => css`
        border-radius: ${view === ProfileView.Masonry ? "6px 6px 0 0" : "6px"};
        padding-bottom: ${() => {
            if (view === ProfileView.Masonry) {
                if ($isProfileMock) return "96.61%";
                else return $imageRatio;
            }
            if (view === ProfileView.TwoColumnThumbnail && !$isPinned) return "44.73%";
            if (view === ProfileView.TwoColumnThumbnail && $isPinned) return "59.75%";
            if (view === ProfileView.OneColumnThumbnail && !$isPinned) return "32.12%";
            if (view === ProfileView.OneColumnThumbnail && $isPinned) return "31.45%";
            if (view === ProfileView.List && $isPinned) return "96.61%";
            if (view === ProfileView.List && !$isPinned) return "75%";
            return null;
        }};
    `)}
`

export const PLIThumbnailImage = styled.img<{ $contain: boolean }>`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: ${({ $contain }) => ($contain ? "contain" : "cover")};
`;

export const PLISourceMetaWrapper = styled.div`
    margin-right: 12px;
    white-space: nowrap;
    max-width: calc(100%);
    text-overflow: ellipsis;
    overflow: hidden;
`;

export const PLISourceMetaIconWrapper = styled.div`
    display: inline-flex;
`;

export const PLISourceMetaDateWrapper = styled.div`
    padding-right: 24px;
    flex-shrink: 0;
    position: relative;

    &:after {
        content:"•";
        width: 6px;
        height: 6px;
        color:  ${({ theme }) => `${theme.colors.blackPearl3}`};
        position: absolute;
        top: 4px;
        right: 9px;
        transform: scale(0.6);
        font-size: ${({ theme }) => {
            if (CUSTOM_FONT_SET_SIZES.includes(theme.customization.profileFontSet)) {
                return "15px";
            }
        }};
    }
`;

export const PLISmallAvatar = styled.div<{ profileSrc?: string }>`
    height: 24px;
    width: 24px;
    margin-right: 10px;
    background-color: grey;
    background-image: ${({ profileSrc }) => profileSrc ? `url(${profileSrc})` : undefined};
    background-size: cover;
    background-repeat: no-repeat;
    border-radius: 50%;
    flex-shrink: 0;
`;

export const PlIListThumb = styled.div<{ $breakpoint?: number }>`
    max-width: 228px;
    padding-left: 20px;
    flex-shrink: 0;

    ${({ $breakpoint }) => $breakpoint && css`
        display: none;

        ${media.custom($breakpoint)} {
            display: block;
        }
    `}
`;

export const PLIViewVisibility = styled(VerticalSpacing)<ResponsiveViewProps & { $hiddenViews: ProfileView[] }>`
    ${({ $viewType, $breakpoint, $hiddenViews }) => responsiveView($viewType, $breakpoint, (view) => css`
        display: ${$hiddenViews.includes(view) ? "none" : "block"};
    `)}
`;

const COMPACT_DATE_BREAKPOINT = theme.v3.layout.sidebarLayout.mobile;

export const PLIDateCompact = styled.span`
    ${media.custom(COMPACT_DATE_BREAKPOINT)} {
        display: none;
    }
`;

export const PLIDateFull = styled.span`
    display: none;

    ${media.custom(COMPACT_DATE_BREAKPOINT)} {
        display: inline;
    }
`;

type OverlayMixinType = { $isMock?: boolean };

const overlayMixin = css<OverlayMixinType>`
    position: relative;
    color: ${({ $isMock }) => $isMock ? "white" : undefined};
    border-radius: ${({ $isMock }) => $isMock ? "6px" : undefined};
    overflow: ${({ $isMock }) => $isMock ? "hidden" : undefined};

    &:after {
        transition: background-color 0.3s ease;
        position: absolute;
        top: 0;
        right: 0;
        content: "";
        width: 100%;
        height: 100%;
        display: ${({ $isMock }) => $isMock ? "block" : "none"};
        background: ${({ theme }) => `${theme.v3.colors.grey96}`};
    }
`;

type ResponsiveClampType = { $breakpoint?: number; $desktopNumLines?: number };

const responsiveClampMixin = css<ResponsiveClampType>`
    ${({ $breakpoint, $desktopNumLines }) => $breakpoint && $desktopNumLines && css`
        ${media.custom($breakpoint)} {
            -webkit-line-clamp: ${$desktopNumLines};
        }
    `}
`;

const UserThemableFeedTitle = styled(V3HeadlineH3)<AuthoryColorSchemeProps>`
    ${UserThemableHeading}
`;

export const V3FeedTitle = styled(UserThemableFeedTitle) <ClampMixinType & ResponsiveClampType & OverlayMixinType>`
    ${ClampMixin};
    ${responsiveClampMixin};
    ${overlayMixin}
`;

const UserThemableFeedDescription = styled(V3Paragraph)<AuthoryColorSchemeProps>`
    ${UserThemableParagraph}
`;

export const V3FeedDescription = styled(UserThemableFeedDescription) <ClampMixinType & ResponsiveClampType & OverlayMixinType>`
    ${ClampMixin};
    ${responsiveClampMixin};
    ${overlayMixin};
    word-break: break-word;
    line-height: 27px;
`;

export const FakeMetadata = styled.div<OverlayMixinType>`
    height: 19px;
    width: 100%;
    max-width: 250px;
    ${overlayMixin}
`;

const SMALL_REDIRECT_BR = 480;

export const PLISourceOriginalWrapper = styled.div`
    flex-shrink: 0;
    position: relative;
    display: flex;
    align-items: center;

    > div {
        &:first-child {
            display: inline-flex;
            padding-left: 16px;
            margin-right: 6px;
        }

        &:nth-child(2) {
            display: none;

            ${media.custom(SMALL_REDIRECT_BR)} {
                display: block;
            };
        }
    }

    &:before {
        content:"•";
        width: 6px;
        height: 6px;
        color:  ${({ theme }) => `${theme.colors.blackPearl3}`};
        position: absolute;
        top: 3px;
        left: 0px;
        transform: scale(0.6);
    }
`;

export const PLIMetadataOriginalLink = styled(PLIMetadataText)`
    text-decoration: none;
    display: block;
    width: auto;
    min-width: 32px;

    ${media.custom(SMALL_REDIRECT_BR)} {
        min-width: 125px;
    };

    &:hover {
        text-decoration: underline;
    }
`;

export const RedirectIconLink = styled.a``;

export const PLIWrapper = styled.div<ResponsiveViewProps & { $dash?: boolean, $isPinned?: boolean, $isMock?: boolean }>`
    ${({ $viewType, $breakpoint, $isPinned }) => responsiveView($viewType, $breakpoint, (view) => css`
        display: ${view === ProfileView.List || ($isPinned && view === ProfileView.TwoColumnThumbnail) ? "flex" : null};

        > div:first-child {
            display: ${view === ProfileView.TwoColumnThumbnail && $isPinned ? "flex" : null};
            align-items: ${view === ProfileView.TwoColumnThumbnail && $isPinned ? "center" : null};
        }
    `)}
    position: relative;
    align-items: center;
    padding: ${({ $isMock  }) =>   $isMock ? "12px" : undefined};
    cursor: ${({ $isMock }) => $isMock ? "pointer" : undefined};
    border:  ${({ $isMock, $viewType, theme}) =>  $viewType === ProfileView.Masonry ?  `1px solid ${hexToRgba(theme.v3.colors.grey88, 0.5)}`  : $isMock ? "1.5px dashed rgba(110, 118, 135, 0.3)" : undefined};
    border-radius: ${({  $viewType  }) =>  $viewType === ProfileView.Masonry ? "12px" : "6px"};
    overflow: ${({  $viewType  }) =>  $viewType === ProfileView.Masonry ? "hidden" : undefined};

    &:hover {
        border: ${({ $isMock }) => $isMock ? "1.5px dashed rgba(110, 118, 135, 0.5)" : undefined};

        ${PLIThumbnail} {
            background: ${({ $isMock, theme }) => $isMock ? `${theme.v3.colors.grey88}` : undefined};
        }

        ${V3FeedTitle},
        ${V3FeedDescription},
        ${FakeMetadata} {
            &:after {
                background: ${({ $isMock, theme }) => $isMock ? `${theme.v3.colors.grey88}` : undefined};
            }
        }
    }

    > div {
        width: 100%;
    }
`;

export const ItemMockAddBtnPos = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    max-width: 85%;
`;

export const SecondaryTextMockWrapper = styled.div`
    max-width: 340px;
    padding: 0 18px;
    text-align: center;
`;